import { computed, ref } from 'vue'
import { freezeObject, isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import truncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'

const TABLE_KEY = 'products'
const UI_VIEW = 'temporary_regulations_locations'
const MINIMUM_MARKERS_TO_SHOW_CHECKBOXES = 1

export function useTable (options) {
  const {
    trans,
    onMapLoadFn,
    fetchMarkersFn,
    locationsMapComponent,
    showMarkersFn,
    totalMarkersCount,
    openInfoDialogFn,
    closeInfoDialogFn,
    openLocationDialogFn
  } = options || {}

  const isLoading = ref(false)
  // Only worth showing checkboxes (the way to reveal a location's marker) once
  // there's more than one marker to actually pick between.
  const showTableCheckboxes = computed(() => (totalMarkersCount?.value || 0) > MINIMUM_MARKERS_TO_SHOW_CHECKBOXES)

  const setLoader = (value) => {
    isLoading.value = value
  }

  const apiConfig = freezeObject({
    get: {
      module: 'road-maintenance',
      route: 'work-order-geolocations'
    }
  })

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance.settings')
      },
      {
        text: trans('locations.locations'),
        bolded: true
      }
    ]
  }))

  const paginationConfig = freezeObject({
    perPageOptions: [50, 100],
    perPage: 50,
    currentPage: 1
  })

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'name',
        label: trans('road-maintenance/excavation-dropdown-type.name')
      },
      {
        key: 'address',
        label: trans('road-maintenance/aggregator.address')
      }
    ]
  }))

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TABLE_KEY] || {}
    if (isFunction(fetchData)) {
      fetchData()
    }

    // The markers go with the listing. Their count is what decides whether the rows
    // offer checkboxes at all, and it is only ever set by this fetch - without it a
    // location added or deleted left both the map and the checkboxes as they were
    // until the page was reloaded.
    if (isFunction(fetchMarkersFn)) {
      fetchMarkersFn()
    }
  }

  const deleteLocationHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`work-order-geolocations/${id}`)
      closeInfoDialogFn()
      fetchTableData()
    }
    catch (exception) {
      console.error(exception)
    }
  }

  const onLocationDelete = (event) => {
    const { description } = event?.data || {}
    const displayDescription = description?.length > 25
      ? description.substring(0, 25) + '...'
      : description || ''

    openInfoDialogFn({
      title: trans('road-maintenance/work_orders.location_delete_title') + (displayDescription ? ' - ' + displayDescription : ''),
      message: trans('road-maintenance/work_orders.location_delete_message'),
      onConfirm: () => deleteLocationHandler(event)
    })
  }

  const onShowLocationDetails = (event) => {
    const { data } = event || {}
    const displayDescription = data?.description?.length > 25
      ? data?.description.substring(0, 25) + '...'
      : data?.description || ''

    openLocationDialogFn({
      title: trans('road-maintenance/work_orders.updating_location') + (displayDescription ? ' - ' + displayDescription : ''),
      editId: event?.data?.id
    })
  }

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowLocationDetails),
    deleteAction(trans, onLocationDelete)
  ]))

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/settings.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('road-maintenance/excavation-dropdown-type.name'),
      key: 'description',
      always_visible: true,
      component: truncatedTextDisplay
    },
    {
      label: trans('road-maintenance/aggregator.address'),
      key: 'address',
      always_visible: true,
      component: truncatedTextDisplay
    }
  ]))

  const onCreateLocation = () => {
    openLocationDialogFn({
      title: trans('road-maintenance/work_orders.creating_location'),
      editId: null
    })
  }

  const config = computed(() => ({
    tableConfig: {
      key: TABLE_KEY,
      uiView: UI_VIEW,
      apiConfig,
      showLoader: isLoading.value,
      filters_and_columns_visibility_config: {
        newBtnPosition: 'start',
        newClick: onCreateLocation
      },
      rowsSelection: {
        showCheckboxes: showTableCheckboxes.value,
        rememberSelectionByDataProperty: 'id',
        selectionsChange: showMarkersFn
      },
      rowDblClick: onShowLocationDetails,
      // A click on a row shows it on the map beside the table
      rowClickIndicator: 'map',
      onRefreshData: fetchMarkersFn,
      sideTableComponent: {
        component: locationsMapComponent,
        mapInitialized: onMapLoadFn
      },
      tableConfig: {
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value,
        paginationConfig
      }
    },
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    setLoader,
    fetchTableData
  }
}
